import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RejectedValue, ErrorResponse, MainResponse, FilteredDropDownResponse, FilteredDropDownRequest, FilteredDropDownModelInit, TranslationList } from 'shared/interfaces/core-interfaces';
import { AuthHeader } from 'services/authHeader';
import { AdditionalInfoRequest, AdditionalInfoResponse, Deal, DealCreateData, DealFileChangeResponse, DealRequest, DealResponse, DealResponsiblesExternal, DealSliceModel, PipelineRequest, PipelineResponse, PlanningRequest, PlanningResponse, UploadDealDocumentsRequest } from './deal-interfaces';
import { SimpleReferenceModel } from "../../../CorporationSettings/referenceDataFeatures/state/reference-data-interface";
import { API_BASE_URL as baseUri } from 'config/env';

export const GetActiveDeals = createAsyncThunk<DealResponse, void, { rejectValue: RejectedValue }>(
    'Slice/DealSlice/GetActiveDeals',
    async (_, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/deals/get-all-active', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as DealResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetInactiveDeals = createAsyncThunk<DealResponse, void, { rejectValue: RejectedValue }>(
    'Slice/DealSlice/GetInactiveDeals',
    async (_, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/deals/get-all-inactive', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as DealResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetDeal = createAsyncThunk<Deal, string, { rejectValue: RejectedValue }>(
    'Slice/DealSlice/GetDeal',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/deals/get?dealId=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const getModel = await response.json() as Deal;
            return getModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetDealCreateData = createAsyncThunk<DealCreateData, void, { rejectValue: RejectedValue }>(
    'Slice/DealSlice/GetDealCreateData',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/deals/get-create-data', {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const getModel = await response.json() as DealCreateData;
            return getModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const CreateDeal = createAsyncThunk<MainResponse, DealRequest, { rejectValue: RejectedValue }>(
    'Slice/DealSlice/CreateDeal',
    async (deal: DealRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/deals/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(deal)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as MainResponse;
            dispatch(GetDeal(createResponse.id));
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateDeal = createAsyncThunk<Deal, DealRequest, { rejectValue: RejectedValue }>(
    'Slice/DealSlice/UpdateDeal',
    async (deal: DealRequest, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/deals/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(deal)
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as Deal;
            dispatch(GetDeal(updateResponse.id));
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UploadDealFiles = createAsyncThunk<DealFileChangeResponse, UploadDealDocumentsRequest, { rejectValue: RejectedValue }>(
    'Slice/DealSlice/UploadDealFiles',
    async (deal: UploadDealDocumentsRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/deals/upload-files', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(deal)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as DealFileChangeResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeleteDealFile = createAsyncThunk<DealFileChangeResponse, string, { rejectValue: RejectedValue }>(
    'Slice/DealSlice/DeleteDealFile',
    async (dealId: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/deals/delete-file?id=' + dealId, {
            method: "DELETE",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const createResponse = await response.json() as DealFileChangeResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeactivateDeal = createAsyncThunk<Deal, string, { rejectValue: RejectedValue }>(
    'Slice/DealSlice/DeactivateDeal',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/deals/deactivate?dealId=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deactivateResponse = await response.json() as Deal;
            return deactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const ReactivateDeal = createAsyncThunk<Deal, string, { rejectValue: RejectedValue }>(
    'Slice/DealSlice/ReactivateDeal',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/deals/reactivate?dealId=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const reactivateResponse = await response.json() as Deal;
            return reactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeleteDeal = createAsyncThunk<Deal, string, { rejectValue: RejectedValue }>(
    'Slice/DealSlice/DeleteDeal',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/deals/delete?dealId=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const reactivateResponse = await response.json() as Deal;
            return reactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetAdditionalInfo = createAsyncThunk<AdditionalInfoResponse, string, { rejectValue: RejectedValue }>(
    'Slice/DealSlice/GetAdditionalInfo',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/deal-additional-infos/get?additionalInfoId=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const getModel = await response.json() as AdditionalInfoResponse;
            return getModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateAdditionalInfo = createAsyncThunk<AdditionalInfoResponse, AdditionalInfoRequest, { rejectValue: RejectedValue }>(
    'Slice/DealSlice/CreateAdditionalInfo',
    async (additionalInfo: AdditionalInfoRequest, { rejectWithValue }) => {
        const updatedAdditionalInfo = {
            ...additionalInfo,
            UDF_Values: additionalInfo.UDF_Values?.map(({ id, value, ...rest }) => ({
                ...rest,
                value: value ? value.toString() : ""
            }))
        };
        const response = await fetch(baseUri + 'api/deal-additional-infos/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(updatedAdditionalInfo)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as AdditionalInfoResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateAdditionalInfo = createAsyncThunk<AdditionalInfoResponse, AdditionalInfoRequest, { rejectValue: RejectedValue }>(
    'Slice/DealSlice/UpdateAdditionalInfo',
    async (additionalInfo: AdditionalInfoRequest, { rejectWithValue }) => {
        const updatedAdditionalInfo = {
            ...additionalInfo,
            UDF_Values: additionalInfo.UDF_Values?.map(({ id, value, ...rest }) => ({
                ...rest,
                value: value ? value.toString() : ""
            }))
        };

        const response = await fetch(baseUri + 'api/deal-additional-infos/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(updatedAdditionalInfo)
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as AdditionalInfoResponse;
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetPlanning = createAsyncThunk<PlanningResponse, string, { rejectValue: RejectedValue }>(
    'Slice/DealSlice/GetPlanning',
    async (dealId: string, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/deal-plannigs/get?dealId=' + dealId, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as PlanningResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CreatePlanning = createAsyncThunk<PlanningResponse, PlanningRequest, { rejectValue: RejectedValue }>(
    'Slice/DealSlice/CreatePlanning',
    async (planning: PlanningRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/deal-plannigs/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(planning)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as PlanningResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const UpdatePlanning = createAsyncThunk<PlanningResponse, PlanningRequest, { rejectValue: RejectedValue }>(
    'Slice/DealSlice/UpdatePlanning',
    async (planning: PlanningRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/deal-plannigs/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(planning)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as PlanningResponse;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

//#region Pipelines

// export const CreatePipeline = createAsyncThunk<TranslationList[], SimpleReferenceModel, { rejectValue: RejectedValue }>(
//     'Slice/DealSlice/CreatePipeline',
//     async (pipeline: SimpleReferenceModel, { rejectWithValue }) => {
//         const response = await fetch(baseUri + 'api/dealsPipelineType/create', {
//             method: "POST",
//             headers: AuthHeader(),
//             body: JSON.stringify(pipeline)
//         });
//         if (response.status === 200) {
//             const createResponse = await response.json() as TranslationList[];
//             return createResponse;
//         }
//         else {
//             const errorResponse = await response.json() as ErrorResponse;
//             return rejectWithValue({ message: errorResponse.detail, status: response.status });
//         }
//     }
// );

//#endregion

//#region Lists Helpers
//Get FIltered partner list
export const GetPartnersForDropdown = createAsyncThunk<FilteredDropDownResponse, FilteredDropDownRequest, { rejectValue: RejectedValue }>(
    'Slice/DealSlice/GetPartnersForDropdown',
    async (data: FilteredDropDownRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partners/drop-down', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const getResponse = await response.json() as FilteredDropDownResponse;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetPartnerDropdownLists = createAsyncThunk<DealResponsiblesExternal, string, { rejectValue: RejectedValue }>(
    'Slice/DealSlice/GetPartnerDropdownLists',
    async (id: string, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/finance/partner-dropdown-lits?id=' + id, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as DealResponsiblesExternal;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);
//#endregion

const initialValue: DealSliceModel = {
    statusCode: '',
    isLoading: false,
    isModalLoading: false,
    message: '',
    showNotification: false,
    listOfActiveDeals: [],
    reloadData: false,
    listOfInactiveDeals: [],
    deal: null,
    dealCreateData: null,
    pipelineInfo: {
        isPipelineDropdownLoading: false,
        newPipelineId: null
    },
    additionalInfo: null,
    planning: null,
    filteredPartnersList: FilteredDropDownModelInit,
    permissions: {
        createEditTenants: [],
        reactivateDeactivateTenants: [],
        deleteTenants: []
    }
}

export const DealSlice = createSlice({
    name: 'DealSlice',
    initialState: initialValue,
    reducers: {
        resetDealSlice: (state) => {
            state.statusCode = '';
            state.isLoading = false;
            state.isModalLoading = false;
            state.reloadData = false;
            state.showNotification = false;
            state.message = '';
            state.listOfActiveDeals = [];
            state.listOfInactiveDeals = [];
            state.deal = null;
            state.dealCreateData = null;
            state.pipelineInfo = {
                isPipelineDropdownLoading: false,
                newPipelineId: null
            }
            state.additionalInfo = null;
            state.planning = null;
            state.filteredPartnersList = FilteredDropDownModelInit;
        },
        resetDeal: (state) => {
            state.deal = null;
            state.isModalLoading = false;
            state.reloadData = false;
            state.pipelineInfo = {
                isPipelineDropdownLoading: false,
                newPipelineId: null
            }
            state.additionalInfo = null;
            state.planning = null;
            state.filteredPartnersList = FilteredDropDownModelInit;
        },
        resetAdditionalInfoFields: (state) => {
            if (state.additionalInfo) {
                state.additionalInfo.additionalFields = undefined;
            }
        },
        resetPipeline: (state) => {
            state.pipelineInfo = {
                isPipelineDropdownLoading: false,
                newPipelineId: null
            }
        },
        resetStatusCode: (state) => {
            state.statusCode = '';
            state.message = '';
            state.showNotification = false;
            state.reloadData = false;
        },
        setSelectedPartners: (state, action: PayloadAction<string>) => {
            if (state.deal) {
                state.deal.clientCompanyId = action.payload
            }
        },
    },
    extraReducers(builder) {
        //GetActiveDeals
        builder
            .addCase(GetActiveDeals.fulfilled, (state, action: PayloadAction<DealResponse>) => {
                state.isLoading = false;
                state.message = '';
                state.listOfActiveDeals = action.payload.deals;
                state.permissions.createEditTenants = action.payload.createEditTenants;
                state.permissions.reactivateDeactivateTenants = action.payload.reactivateDeactivateTenants;
                state.permissions.deleteTenants = action.payload.deleteTenants;
            })
            .addCase(GetActiveDeals.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetActiveDeals.pending, state => {
                state.isLoading = true;
            })
        //GetInactiveDeals
        builder
            .addCase(GetInactiveDeals.fulfilled, (state, action: PayloadAction<DealResponse>) => {
                state.isLoading = false;
                state.message = '';
                state.listOfInactiveDeals = action.payload.deals;
            })
            .addCase(GetInactiveDeals.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetInactiveDeals.pending, state => {
                state.isLoading = true;
            })
        //GetDeal
        builder
            .addCase(GetDeal.fulfilled, (state, action: PayloadAction<Deal>) => {
                state.isModalLoading = false;
                state.message = '';
                state.deal = action.payload;
            })
            .addCase(GetDeal.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetDeal.pending, state => {
                state.isModalLoading = true;
            })
        //GetDealCreateData
        builder
            .addCase(GetDealCreateData.fulfilled, (state, action: PayloadAction<DealCreateData>) => {
                state.isModalLoading = false;
                state.message = '';
                state.dealCreateData = action.payload;
            })
            .addCase(GetDealCreateData.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetDealCreateData.pending, state => {
                state.isModalLoading = true;
            })
        //CreateDeal
        builder
            .addCase(CreateDeal.fulfilled, (state, action) => {
                state.message = 'The deal is successfully created.';
                state.statusCode = "200";
                state.isModalLoading = false;
                state.showNotification = true;
            })
            .addCase(CreateDeal.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateDeal.pending, state => {
                state.isModalLoading = true;
            })
        //UpdateDeal
        builder
            .addCase(UpdateDeal.fulfilled, (state, action: PayloadAction<Deal>) => {
                state.statusCode = "200";
                state.isModalLoading = false;
                state.message = 'The deal is successfully updated.';
                state.showNotification = true;
                //state.deal = action.payload;
            })
            .addCase(UpdateDeal.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateDeal.pending, state => {
                state.isModalLoading = true;
            })
        //UploadDealFiles
        builder
            .addCase(UploadDealFiles.fulfilled, (state, action: PayloadAction<DealFileChangeResponse>) => {
                state.statusCode = "200";
                // state.isModalLoading = false;
                state.message = action.payload.message ?? 'The deal file is successfully uploaded.';
                state.showNotification = true;
                if (state.deal) {
                    state.deal.files = action.payload.files;
                }
            })
            .addCase(UploadDealFiles.rejected, (state, action) => {
                // state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UploadDealFiles.pending, state => {
                // state.isModalLoading = true;
            })
        //DeleteDealFile
        builder
            .addCase(DeleteDealFile.fulfilled, (state, action: PayloadAction<DealFileChangeResponse>) => {
                state.statusCode = "200";
                state.isModalLoading = false;
                state.message = action.payload.message ?? 'The deal file is successfully deleted.';
                state.showNotification = true;
                if (state.deal) {
                    state.deal.files = action.payload.files;
                }
            })
            .addCase(DeleteDealFile.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeleteDealFile.pending, state => {
                // state.isModalLoading = true;
            })
        //DeactivateDeal
        builder
            .addCase(DeactivateDeal.fulfilled, (state, action: PayloadAction<Deal>) => {
                state.statusCode = "200";
                state.isLoading = false;
                state.message = 'The deal is successfully deactivated.';
                state.showNotification = true;
                state.reloadData = true;
            })
            .addCase(DeactivateDeal.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeactivateDeal.pending, state => {
                state.isModalLoading = true;
            })
        //ReactivateDeal
        builder
            .addCase(ReactivateDeal.fulfilled, (state, action: PayloadAction<Deal>) => {
                state.statusCode = "200";
                state.isLoading = false;
                state.message = 'The deal is successfully reactivated.';
                state.showNotification = true;
                state.reloadData = true;
            })
            .addCase(ReactivateDeal.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(ReactivateDeal.pending, state => {
                state.isModalLoading = true;
            })
        //DeleteDeal
        builder
            .addCase(DeleteDeal.fulfilled, (state, action: PayloadAction<Deal>) => {
                state.statusCode = "200";
                state.isLoading = false;
                state.message = 'The deal is successfully deleted.';
                state.showNotification = true;
            })
            .addCase(DeleteDeal.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeleteDeal.pending, state => {
                state.isModalLoading = true;
            })
        //CreatePipeline
        // builder
        //     .addCase(CreatePipeline.fulfilled, (state, action) => {
        //         state.pipelineInfo.isPipelineDropdownLoading = false;
        //         state.message = 'The pipeline is successfully created.';
        //         state.statusCode = "200";
        //         state.showNotification = false;
        //         state.pipelineInfo = {
        //             isPipelineDropdownLoading: false,
        //             newPipelineId: ""
        //         }
        //         if (state.dealCreateData) {
        //             state.dealCreateData.pipelines = action.payload;
        //         }
        //     })
        //     .addCase(CreatePipeline.rejected, (state, action) => {
        //         state.message = action.payload?.message ?? 'Error in application';
        //         state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
        //         state.showNotification = true;
        //         state.pipelineInfo.isPipelineDropdownLoading = false;
        //     })
        //     .addCase(CreatePipeline.pending, state => {
        //         state.pipelineInfo.isPipelineDropdownLoading = true;
        //     })
        //GetAdditionalInfo
        builder
            .addCase(GetAdditionalInfo.fulfilled, (state, action: PayloadAction<AdditionalInfoResponse>) => {
                state.isModalLoading = false;
                state.message = '';
                state.additionalInfo = action.payload;
            })
            .addCase(GetAdditionalInfo.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetAdditionalInfo.pending, state => {
                state.isModalLoading = true;
            })
        //CreateAdditionalInfo
        builder
            .addCase(CreateAdditionalInfo.fulfilled, (state, action) => {
                state.message = 'The additional info is successfully created.';
                state.isModalLoading = false;
                state.statusCode = "200";
                state.showNotification = true;
                state.additionalInfo = {
                    ...action.meta.arg,
                    additionalFields: action.meta.arg.UDF_Values,
                    id: action.payload.id,
                };
            })
            .addCase(CreateAdditionalInfo.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateAdditionalInfo.pending, state => {
                state.isModalLoading = true;
            })
        //UpdateAdditionalInfo
        builder
            .addCase(UpdateAdditionalInfo.fulfilled, (state, action) => {
                state.statusCode = "200";
                state.isModalLoading = false;
                state.message = 'The additional info is successfully updated.';
                state.showNotification = true;
                state.additionalInfo = {
                    ...action.meta.arg,
                    additionalFields: action.meta.arg.UDF_Values,
                    id: action.payload.id,
                };
            })
            .addCase(UpdateAdditionalInfo.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateAdditionalInfo.pending, state => {
                state.isModalLoading = true;
            })
        //Get Planning
        builder
            .addCase(GetPlanning.fulfilled, (state, action: PayloadAction<PlanningResponse>) => {
                state.planning = action.payload
                state.message = '';
                state.isModalLoading = false;
                state.showNotification = true;
            })
            .addCase(GetPlanning.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(GetPlanning.pending, state => {
                state.isModalLoading = true;
            })
        //Create Planning
        builder
            .addCase(CreatePlanning.fulfilled, (state, action: PayloadAction<PlanningResponse>) => {
                state.planning = action.payload;
                state.message = 'The planning is successfully created.';
                state.statusCode = "200";
                state.isModalLoading = false;
                state.showNotification = true;
            })
            .addCase(CreatePlanning.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreatePlanning.pending, state => {
                state.isModalLoading = true;
            })
        //Update PLanning
        builder
            .addCase(UpdatePlanning.fulfilled, (state, action: PayloadAction<PlanningResponse>) => {
                state.planning = action.payload;
                state.statusCode = "200";
                state.isModalLoading = false;
                state.message = 'The planning is successfully updated.';
                state.showNotification = true;
            })
            .addCase(UpdatePlanning.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdatePlanning.pending, state => {
                state.isModalLoading = true;
            })
        //Get Filtered Partners list    
        builder.addCase(GetPartnersForDropdown.pending, (state) => {
            state.filteredPartnersList.loading = true;
            state.filteredPartnersList.error = null;
        })
            .addCase(GetPartnersForDropdown.fulfilled, (state, action) => {
                state.filteredPartnersList.loading = false;
                state.filteredPartnersList.data = action.payload.data;
                state.filteredPartnersList.totalItems = action.payload.totalItems;
                state.filteredPartnersList.totalPages = action.payload.totalPages;
                state.filteredPartnersList.currentPage = action.payload.currentPage;
            })
            .addCase(GetPartnersForDropdown.rejected, (state, action) => {
                state.filteredPartnersList.loading = false;
                state.filteredPartnersList.error = action.payload?.message || 'Error fetching data';
            });
        //Get Partner Dropdown Lists For External
        builder
            .addCase(GetPartnerDropdownLists.fulfilled, (state, action: PayloadAction<DealResponsiblesExternal>) => {
                state.isModalLoading = false;
                if (state.dealCreateData) {
                    state.dealCreateData.clientResponsibles = action.payload.contacts;
                }
            })
            .addCase(GetPartnerDropdownLists.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetPartnerDropdownLists.pending, state => {
                //state.isModalLoading = true;
            })
    },
});

export const { resetDeal, resetDealSlice, resetStatusCode, resetPipeline, resetAdditionalInfoFields, setSelectedPartners } = DealSlice.actions;
export default DealSlice.reducer;